import { useEffect, useRef, useState } from 'react'
import {
  Box, Divider, Switch, FormControlLabel, Typography, CircularProgress, Tooltip, Chip,
} from '@mui/material'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import ShortTextIcon from '@mui/icons-material/ShortText'
import SubjectIcon from '@mui/icons-material/Subject'
import SelectAllIcon from '@mui/icons-material/SelectAll'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import CloseIcon from '@mui/icons-material/Close'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import { addToast } from '../services/Toasts'
import taapi from '../services/taapi'
import { DEBUG_MODE } from '../common/common'
import DebugJsonButton from '../comps/DebugJsonButton'
import { useQuestionProcessing } from '../services/TaskManagerService'
import { useApp, useAppActions, useAppProject, useNav, useTempQuestion } from '../AppContext'
import { FancyButton } from '../comps/FancyButtons'
import TaResponse from '../comps/TaResponse'
import TabHeader from '../comps/TabHeader'
import TaActionButton from '../comps/TaActionButton'
import TraceArc from '../comps/TraceArc'
import ScoreArc from '../comps/ScoreArc'
import TaWordCount from '../comps/TaWordCount'
import ChatInputBar from '../comps/ChatInputBar'
import ConfirmModal from './modals/ConfirmModal'
import { FS_SM, FS_XS, ICON_SM, ICON_LG, ICON_NAV, FS_XL } from '../common/theme'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'


type ViewMode = 'basic' | 'pro'

// One row in the chat history. User turns carry the raw tweak
// instruction; assistant turns carry the streamed model response.
interface ChatTurn {
  type: 'user' | 'assistant'
  text: string
}

// A version is a pair of temp-question shas — the "before" (original)
// and the "after" (modified) of a single action. v1.original is the
// snapshot of the live question at referral time; the first action's
// result becomes v1.modified. Every subsequent action creates a new
// version whose `originalSha` is a fresh clone of the previous
// version's `modifiedSha`, so the version chain reads left-to-right as
// v1.orig → v1.mod → v2.orig → v2.mod → v3.orig → …
// The version chips swap both panes at once via `currentIter`.
interface Version {
  originalSha: string
  modifiedSha?: string
}

// Clone the answerstructure and drop the per-sentence diff fields
// (`segments`, `markup`, `markupmod`) — they exist solely to drive the
// diff overlay in the modified pane and are meaningless on the live
// question. `sentence` is already the final plain text under the
// segments contract, so no character-level stripping is needed. Used
// by `handleApply` so the applied answer is plain with no diff residue.
function cleanAnswerstructureForApply(struct: any): any {
  if (!struct) return struct
  return {
    ...struct,
    paragraphs: (struct.paragraphs ?? []).map((p: any) => ({
      ...p,
      lines: (p.lines ?? []).map((l: any) => ({
        ...l,
        sentences: (l.sentences ?? []).map((s: any) => {
          const { segments, markup, markupmod, ...rest } = s ?? {}
          void segments; void markup; void markupmod
          return rest
        }),
      })),
    })),
  }
}

function countCheckedSentences(structure: any): number {
  if (!structure?.paragraphs) return 0
  let n = 0
  for (const p of structure.paragraphs) {
    for (const l of p.lines ?? []) {
      for (const s of l.sentences ?? []) {
        if (s?.ischecked) n++
      }
    }
  }
  return n
}

// Full-tab port of the legacy `TaAdvEditModal`. Same behaviour: an
// "Original vs Modified" side-by-side answer view over the currently
// focused question, driven by a chat panel below that streams
// /api2/chatstream results into a synth question sitting on the right
// pane. Apply commits the modified answerobj back to the live question.
// Sourcing the question from `useAppProject().currentQuestion` matches
// ScoreFixTab / ResponseBuilderTab — the caller flips
// `setCurrentQuestion(q)` + `setActiveTab('advedit')` to open it.
export default function AdvancedEditTab() {
  const { currentQuestion: liveQuestion } = useAppProject()
  const { currentQuestionSetTrigger } = useApp()
  const { updateQuestion, buildTempQuestion, getTempQuestion } = useAppActions()
  const { setActiveTab } = useNav()

  const [viewMode, setViewMode] = useState<ViewMode>('basic')
  const [wholeAnswer, setWholeAnswer] = useState(true)
  const [isProcessing, setIsProcessing] = useState(false)

  // Version list — populated on referral with `[{ originalSha: <snapshot> }]`
  // and grown by each chat-tweak. `currentIter` selects the active pair;
  // `originalSha` / `modifiedSha` are derived from it. Every `updateQuestion`
  // patch against either sha routes through `TempContext` and this
  // component re-renders naturally.
  const [versions, setVersions] = useState<Version[]>([])
  const [currentIter, setCurrentIter] = useState(-1)
  const currentVersion = currentIter >= 0 ? versions[currentIter] : undefined
  const originalSha = currentVersion?.originalSha
  const modifiedSha = currentVersion?.modifiedSha
  const originalQuestion: any = useTempQuestion(originalSha)
  const modifiedQuestion: any = useTempQuestion(modifiedSha)
  const question: any = originalQuestion

  // Processing flags per pane — keyed by each pane's own temp sha so
  // the loading spinners track their respective Generate runs
  // independently. Switching version flips `originalSha` / `modifiedSha`,
  // which re-subscribes each hook, so the arcs stay linked to whichever
  // pair the user is currently viewing.
  const origProcessing = useQuestionProcessing(originalSha ?? '')
  const modProcessing = useQuestionProcessing(modifiedSha ?? '')
  const origScoring = !!origProcessing.isrunningscoring
  const origTracing = !!origProcessing.isrunningtrace
  const modScoring = !!modProcessing.isrunningscoring
  const modTracing = !!modProcessing.isrunningtrace

  const [turns, setTurns] = useState<ChatTurn[]>([])
  const [streamingText, setStreamingText] = useState<string | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const [clearVersionConfirmOpen, setClearVersionConfirmOpen] = useState(false)

  const [chatHeight, setChatHeight] = useState(260)
  const isVResizingRef = useRef(false)
  const startYRef = useRef(0)
  const startHeightRef = useRef(260)
  const splitContainerRef = useRef<HTMLDivElement>(null)
  // Horizontal Original / Modified split — matches BidQuestion's
  // resizable divider. `leftPct` is the Original pane's width as a
  // percent of the row; the handle drags it between 20-80%.
  const [leftPct, setLeftPct] = useState(50)
  const horizPaneRef = useRef<HTMLDivElement>(null)
  const handleHorizResizeStart = (e: React.MouseEvent) => {
    e.preventDefault()
    const container = horizPaneRef.current
    if (!container) return
    const onMove = (ev: MouseEvent) => {
      const rect = container.getBoundingClientRect()
      const pct = ((ev.clientX - rect.left) / rect.width) * 100
      setLeftPct(Math.max(20, Math.min(80, pct)))
    }
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  // Whenever the referred question changes, snapshot it into a fresh
  // synth (the "Original") and clear all downstream state — modified
  // versions, current-iteration pointer, chat history, processing
  // flag. The modified pane starts blank; the first chat-tweak result
  // creates iteration 0.
  useEffect(() => {
    if (!liveQuestion) {
      setVersions([])
      setCurrentIter(-1)
      setTurns([])
      setIsProcessing(false)
      return
    }
    // Seed v1 with a temp snapshot of the live question as its
    // `originalSha`. `modifiedSha` stays undefined until the first
    // chat-tweak lands.
    const orig = buildTempQuestion(liveQuestion, {})
    if (orig) {
      setVersions([{ originalSha: orig.questionsha }])
      setCurrentIter(0)
    } else {
      setVersions([])
      setCurrentIter(-1)
    }
    setTurns([])
    setIsProcessing(false)
  }, [currentQuestionSetTrigger])

  const switchIteration = (i: number) => {
    if (i < 0 || i >= versions.length) return
    // Both panes are derived from `versions[currentIter]`, so a single
    // `setCurrentIter` swap flips the Original + Modified together.
    setCurrentIter(i)
  }

  // Drop the current version pair and jump back to the previous one.
  // Only meaningful when there are 2+ versions — clearing the initial
  // referral snapshot would leave the tab in an incoherent state, so
  // the button is gated on `versions.length > 1` (same gate as the
  // chip row). Fires from `ConfirmModal` so the user has a chance to
  // cancel before losing the tweak.
  const clearCurrentVersion = () => {
    if (versions.length <= 1) { setClearVersionConfirmOpen(false); return }
    setVersions(prev => prev.filter((_, i) => i !== currentIter))
    setCurrentIter(prev => Math.max(0, prev - 1))
    setClearVersionConfirmOpen(false)
  }

  const handleFixedStructureChange = (next: any) => {
    // Route through `updateQuestion` (not in-place mutation) so the
    // temp registry's map reference actually changes. That triggers
    // `TempContext` to re-issue, this tab re-renders, and TaResponse
    // sees the fresh `structure` prop matching its DOM — otherwise its
    // internal sync effect fires (on any incidental re-render, e.g.
    // BasicView's `setHasTypedContent` on blur) and reverts the DOM
    // back to the stale structure prop.
    if (!modifiedSha) return
    updateQuestion(modifiedSha, {
      answerobj: { ...(modifiedQuestion?.answerobj ?? {}), answerstructure: next },
      bideval: null as any,
    })
  }

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [turns.length])
  useEffect(() => {
    if (streamingText !== null) messagesEndRef.current?.scrollIntoView({ behavior: 'auto' })
  }, [streamingText])

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!isVResizingRef.current) return
      const delta = startYRef.current - e.clientY
      const maxH = (splitContainerRef.current?.clientHeight ?? window.innerHeight) - 80
      setChatHeight(Math.max(80, Math.min(maxH, startHeightRef.current + delta)))
    }
    const onMouseUp = () => {
      if (isVResizingRef.current) {
        isVResizingRef.current = false
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
    }
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
    return () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }
  }, [])

  const handleVertResizeStart = (e: React.MouseEvent) => {
    e.preventDefault()
    isVResizingRef.current = true
    startYRef.current = e.clientY
    startHeightRef.current = chatHeight
    document.body.style.cursor = 'row-resize'
    document.body.style.userSelect = 'none'
  }

  const originalStructure = question?.answerobj?.answerstructure ?? null
  const selectedSentenceCount = countCheckedSentences(modifiedQuestion?.answerobj?.answerstructure)

  const setAllSentencesChecked = (checked: boolean) => {
    const next = structuredClone(modifiedQuestion?.answerobj?.answerstructure)
    for (const p of next?.paragraphs ?? []) {
      for (const l of p.lines ?? []) {
        for (const s of l.sentences ?? []) {
          if (!s.pin) s.ischecked = checked
        }
      }
    }
    handleFixedStructureChange(next)
  }

  const runTweak = async (instruction: string) => {
    const text = instruction.trim()
    if (!text || isProcessing || !question) return
    const now = new Date().toISOString()
    const messagelist = [
      ...turns.map(t => ({ type: t.type, q: t.text, updatetime: now })),
      { type: 'user' as const, q: text, updatetime: now },
    ]
    setTurns(prev => [...prev, { type: 'user', text }])
    setIsProcessing(true)
    setStreamingText('')
    let fullText = ''
    const controller = new AbortController()
    abortControllerRef.current = controller
    // Strip the diff fields (`segments` / `markup` / `markupmod`) from
    // the outbound question. The backend expects plain answerstructure —
    // leaving them on would confuse the next diff pass.
    const sourceQuestion: any = modifiedQuestion ?? question
    const cleanQuestion = {
      ...sourceQuestion,
      answerobj: {
        ...(sourceQuestion.answerobj ?? {}),
        answerstructure: cleanAnswerstructureForApply(sourceQuestion.answerobj?.answerstructure),
      },
    }
    try {
      for await (const data of taapi.openStreamSocket('/api2/chatstream', {
        question: cleanQuestion,
        messagelist,
        systemprompt: null,
        convosha: null,
        title: null,
        chatreferences: null,
      }, controller.signal)) {
        if (DEBUG_MODE)
          console.log(data);
        if (data === '[DONE]') break
        let parsed: { text?: string; error?: string; questionresponse?: any }
        try { parsed = JSON.parse(data) } catch { continue }
        if (parsed.error) throw new Error(parsed.error)
        if (parsed.text) {
          fullText += parsed.text
          setStreamingText(fullText)
        }
        if (parsed.questionresponse?.answerobj?.answerstructure) {
          const r = parsed.questionresponse
          const cur = versions[currentIter]
          if (!cur) continue

          // First action for the current version reuses its existing
          // original (the referral snapshot). Subsequent actions spawn
          // a fresh v(N+1) whose "before" is a clone of the previous
          // modified — so the chain reads left-to-right as
          // v1.orig → v1.mod → v2.orig → v2.mod → …
          const isFirstAction = !cur.modifiedSha
          const beforeSha = isFirstAction
            ? cur.originalSha
            : buildTempQuestion(getTempQuestion(cur.modifiedSha!) ?? question)?.questionsha
          if (!beforeSha) continue

          // Stamp the response's `originalanswerstructure` on the
          // before pane so the diff segments describing this tweak
          // highlight on the left too. Same call for both branches —
          // only the target sha differs.
          const beforeQ = getTempQuestion(beforeSha)
          updateQuestion(beforeSha, {
            answerobj: { ...(beforeQ?.answerobj ?? {}), answerstructure: r.originalanswerstructure },
          })

          // Build the modified pane. `buildTempQuestion` merges the
          // full server-returned answerobj so simint / structure /
          // sourcematch land on it together, and stamps a fresh
          // `answersha` on top.
          const newMod = buildTempQuestion(beforeQ ?? question, {
            answerobj: r.answerobj,
            bideval: r.bideval ?? null,
          })
          if (!newMod) continue

          if (isFirstAction) {
            setVersions(prev => prev.map((v, i) =>
              i === currentIter ? { ...v, modifiedSha: newMod.questionsha } : v
            ))
          } else {
            setVersions(prev => [...prev, {
              originalSha: beforeSha,
              modifiedSha: newMod.questionsha,
            }])
            setCurrentIter(versions.length)
          }
        }
      }
      setTurns(prev => [...prev, { type: 'assistant', text: fullText || '(no response)' }])
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        if (fullText) setTurns(prev => [...prev, { type: 'assistant', text: fullText }])
      } else {
        setTurns(prev => [...prev, { type: 'assistant', text: 'Chat failed.' }])
        addToast('error', 'Chat failed')
      }
    } finally {
      abortControllerRef.current = null
      setStreamingText(null)
      setIsProcessing(false)
    }
  }

  const handleStop = () => {
    abortControllerRef.current?.abort()
  }

  // Commit the modified answerobj onto the live question and jump back
  // to the Builder tab so the user sees the applied change in context.
  const handleApply = () => {
    if (!liveQuestion || !modifiedQuestion) return
    // Strip inline diff spans and drop the wizard-only `markup` /
    // `markupmod` fields — those exist for the diff overlay and must
    // never land on the live question.
    const cleanAnswerobj = {
      ...(modifiedQuestion.answerobj ?? {}),
      answerstructure: cleanAnswerstructureForApply(modifiedQuestion.answerobj?.answerstructure),
    }
    // Commit both the answer and the bideval to the LIVE question
    // (not the original snapshot). They were computed against this
    // iteration's synth (the arc's Generate button runs score / trace
    // on the modified sha), so applying the answer without the
    // matching bideval would leave a stale score on the live question.
    updateQuestion(liveQuestion.questionsha, {
      answerobj: cleanAnswerobj,
      bideval: modifiedQuestion.bideval,
    })
    addToast('success', 'Applied to question.')
    setActiveTab('response')
  }

  const wholeAnswerToggle = (
    <FormControlLabel
      sx={{ ml: 0.25, mr: 0 }}
      control={
        <Switch
          size="small"
          checked={wholeAnswer}
          onChange={(_, checked) => setWholeAnswer(checked)}
          data-testid="adv_wholeanswer_toggle"
        />
      }
      label={
        <Typography variant="caption" sx={{ fontSize: FS_XS }}>
          {wholeAnswer ? 'Whole Answer' : 'Sentences'}
        </Typography>
      }
    />
  )

  const quickTweakButtons = (
    <>
      <Tooltip title="Reword">
        <Box component="span">
          <TaActionButton
            testId="adv_reword_btn"
            icon={<AutorenewIcon sx={{ fontSize: ICON_SM }} />}
            disabled={isProcessing}
            onClick={() => runTweak('Please reword the following text')}
          />
        </Box>
      </Tooltip>
      <Tooltip title="Shorten">
        <Box component="span">
          <TaActionButton
            testId="adv_shorten_btn"
            icon={<ShortTextIcon sx={{ fontSize: ICON_SM }} />}
            disabled={isProcessing}
            onClick={() => runTweak('Please shorten the following text')}
          />
        </Box>
      </Tooltip>
      <Tooltip title="Expand">
        <Box component="span">
          <TaActionButton
            testId="adv_expand_btn"
            icon={<SubjectIcon sx={{ fontSize: ICON_SM }} />}
            disabled={isProcessing}
            onClick={() => runTweak('Please expand the following text')}
          />
        </Box>
      </Tooltip>
    </>
  )

  // No question in context — surface a placeholder so the tab doesn't
  // silently render an empty split view. Matches ScoreFixTab's
  // "pick a question" feel without pulling in the full picker.
  if (!question) {
    return (
      <Box
        data-testid="advedit_empty"
        sx={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          gap: 1, opacity: 0.55, textAlign: 'center', px: 3,
        }}
      >
        <SmartToyOutlinedIcon sx={{ fontSize: 40 }} />
        <Typography variant="caption" sx={{ fontSize: FS_SM }}>
          Open a question from the Builder or Bid Manager to start an advanced edit.
        </Typography>
      </Box>
    )
  }

  return (
    <Box data-testid="advedit_root" sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>

      {/* View tabs - shared across both panes */}
      <TabHeader<ViewMode>
        tabs={[
          { key: 'basic', label: 'Text' },
          { key: 'pro', label: 'Trace' },
        ]}
        active={viewMode}
        onChange={setViewMode}
        testIdPrefix="adv_view"
      />

      <Box ref={splitContainerRef} sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>

        {/* ── Top: Original vs Modified ───────────────────────────── */}
        <Box ref={horizPaneRef} sx={{ flex: 1, display: 'flex', minHeight: 0 }}>

          {/* Original (read-only) */}
          <Box sx={{ width: `${leftPct}%`, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <Box sx={{
              px: 1.5, py: 0.75, borderBottom: 1, borderColor: 'divider', bgcolor: 'action.hover',
              display: 'flex', alignItems: 'center', gap: 0.75, minHeight: 40, flexShrink: 0,
            }}>
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: FS_XS }}>Original</Typography>
              <TaWordCount
                structure={question?.answerobj?.answerstructure}
                replylimit={question?.replylimit}
                question={question}
              />
              {DEBUG_MODE && (
                <DebugJsonButton
                  value={question}
                  title={`Question JSON · ${question?.questionsha ?? ''}`}
                  testIdPrefix="adv_debug_original"
                />
              )}
              <Box sx={{ flex: 1 }} />
              <TraceArc
                question={question}
                loading={origTracing}
                tooltip="Original trace similarity"
                modalTitle="Trace Similarity - Original"
              />
              <ScoreArc
                question={question}
                loading={origScoring}
                tooltip="Original estimated score"
                modalTitle="Estimated Score - Original"
              />

            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, minHeight: 0 }}>
              <TaResponse
                structure={originalStructure}
                onChange={() => { /* read-only */ }}
                editable={false}
                showOriginal
                id="adv_original_basic"
              />
            </Box>
          </Box>

          {/* Resize handle — drag to reflow Original / Modified widths.
              Same shape as BidQuestion's `handleResizeStart` divider. */}
          <Box
            onMouseDown={handleHorizResizeStart}
            data-testid="adv_horiz_resize_handle"
            sx={{
              width: 7,
              flexShrink: 0,
              cursor: 'col-resize',
              borderLeft: 1,
              borderRight: 1,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <MoreVertIcon sx={{ fontSize: ICON_LG, opacity: 0.35 }} />
          </Box>

          {/* Modified (editable) */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <Box sx={{
              px: 1.5, py: 0.75, borderBottom: 1, borderColor: 'divider', bgcolor: 'action.hover',
              display: 'flex', alignItems: 'center', gap: 1, minHeight: 40, flexShrink: 0,
            }}>
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: FS_XS }}>
                {versions.length > 1 ? `Modified v${currentIter + 1}` : 'Modified'}
              </Typography>
              {modifiedSha && (
                <TaWordCount
                  structure={modifiedQuestion?.answerobj?.answerstructure}
                  replylimit={question?.replylimit}
                  question={modifiedQuestion}
                />
              )}
              {versions.length > 1 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, ml: 0.5 }}>
                  {versions.map((_, i) => (
                    <Chip
                      key={i}
                      data-testid={`adv_iter_chip_${i}`}
                      size="small"
                      label={`v${i + 1}`}
                      color={i === currentIter ? 'primary' : 'default'}
                      variant={i === currentIter ? 'filled' : 'outlined'}
                      onClick={() => switchIteration(i)}
                      sx={{ height: 20, fontSize: '0.7rem', cursor: 'pointer' }}
                    />
                  ))}
                  <Tooltip title="Clear current version">
                    <TaActionButton
                      testId="adv_clear_version_btn"
                      icon={<CancelOutlinedIcon sx={{ fontSize: FS_XL }} />}
                      onClick={() => setClearVersionConfirmOpen(true)}
                    />
                  </Tooltip>
                </Box>
              )}
              {!wholeAnswer && viewMode === 'pro' && (
                <>
                  {selectedSentenceCount > 0 && (
                    <Chip
                      size="small"
                      data-testid="adv_selected_chip"
                      label={`${selectedSentenceCount} selected`}
                      color="primary"
                      onDelete={() => setAllSentencesChecked(false)}
                      sx={{ fontSize: '0.7rem', height: 20, ml: 1 }}
                    />
                  )}
                  <Tooltip title="Select all sentences">
                    <span>
                      <TaActionButton
                        testId="adv_selectall_btn"
                        icon={<SelectAllIcon sx={{ fontSize: ICON_SM }} />}
                        onClick={() => setAllSentencesChecked(true)}
                      />
                    </span>
                  </Tooltip>
                </>
              )}
              <Box sx={{ flex: 1 }} />
              {modifiedSha && (
                <TraceArc
                  loading={modTracing}
                  tooltip="Modified trace similarity"
                  question={modifiedQuestion}
                  modalTitle="Trace Similarity - Modified"
                />
              )}
              {modifiedSha && (
                <ScoreArc
                  loading={modScoring}
                  tooltip="Modified estimated score"
                  question={modifiedQuestion}
                  modalTitle="Estimated Score - Modified"
                />
              )}
              {DEBUG_MODE && modifiedQuestion && (
                <DebugJsonButton
                  value={modifiedQuestion}
                  title={`Modified Question JSON · ${modifiedQuestion?.questionsha ?? ''}`}
                  tooltip="Inspect modified question JSON (debug)"
                  testIdPrefix="adv_debug"
                />
              )}
              <Typography variant="caption" sx={{ fontSize: FS_XS, opacity: 0.45, ml: 0.5 }}>
                AI can make mistakes
              </Typography>
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, minHeight: 0 }}>
              {isProcessing && !modifiedSha ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : !modifiedSha ? (
                <Box
                  data-testid="adv_modified_empty"
                  sx={{
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    height: '100%', gap: 1, opacity: 0.5, textAlign: 'center', px: 2,
                  }}
                >
                  <SmartToyOutlinedIcon sx={{ fontSize: 32 }} />
                  <Typography variant="caption" sx={{ fontSize: FS_SM }}>
                    Ask the AI to modify your answer
                  </Typography>
                </Box>
              ) : (
                <TaResponse
                  structure={modifiedQuestion?.answerobj?.answerstructure}
                  onChange={handleFixedStructureChange}
                  modificationMode
                  id="adv_modified_basic"
                />
              )}
            </Box>
          </Box>
        </Box>

        {/* ── Resize handle ───────────────────────────────────────── */}
        <Box
          onMouseDown={handleVertResizeStart}
          data-testid="adv_resize_handle"
          sx={{
            height: 7, flexShrink: 0,
            cursor: 'row-resize !important',
            borderTop: 1, borderBottom: 1, borderColor: 'divider',
            bgcolor: 'background.paper',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            '& *': { cursor: 'row-resize !important' },
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <MoreHorizIcon sx={{ fontSize: ICON_NAV, opacity: 0.35 }} />
        </Box>

        {/* ── Bottom: chat history + chat input ───────────────────── */}
        <Box sx={{
          flex: `0 0 ${chatHeight}px`,
          display: 'flex', flexDirection: 'column',
          minHeight: 0, overflow: 'hidden',
        }}>
          <Box data-testid="adv_chat_messages" sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 1 }}>
            {turns.length === 0 && streamingText === null ? (
              <Box sx={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                height: '100%', gap: 1, opacity: 0.5,
              }}>
                <SmartToyOutlinedIcon sx={{ fontSize: 32 }} />
                <Typography variant="caption" sx={{ fontSize: FS_SM }}>
                  Ask the assistant about this question
                </Typography>
              </Box>
            ) : (
              <>
                {turns.map((t, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: 'flex',
                      justifyContent: t.type === 'user' ? 'flex-end' : 'flex-start',
                      mb: 1,
                    }}
                  >
                    <Box sx={{
                      maxWidth: '72%',
                      px: 1.25, py: 0.75,
                      borderRadius: 1.5,
                      bgcolor: t.type === 'user' ? 'primary.main' : 'action.hover',
                      color: t.type === 'user' ? 'common.white' : 'text.primary',
                    }}>
                      <Typography variant="caption" sx={{ fontSize: FS_SM, whiteSpace: 'pre-wrap' }}>
                        {t.text}
                      </Typography>
                    </Box>
                  </Box>
                ))}
                {streamingText !== null && (
                  <Box
                    data-testid="adv_chat_streaming"
                    sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}
                  >
                    <Box sx={{
                      maxWidth: '72%',
                      px: 1.25, py: 0.75,
                      borderRadius: 1.5,
                      bgcolor: 'action.hover',
                      color: 'text.primary',
                    }}>
                      <Typography variant="caption" sx={{ fontSize: FS_SM, whiteSpace: 'pre-wrap' }}>
                        {streamingText || '…'}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </>
            )}
            <div ref={messagesEndRef} />
          </Box>
          <Divider />

          <ChatInputBar
            testId="adv_chat_input"
            onSend={(text) => runTweak(text)}
            onStop={handleStop}
            busy={isProcessing}
            placeholder="Ask the workflow assistant about this question..."
            toolbarStartSlot={wholeAnswerToggle}
            toolbarEndSlot={quickTweakButtons}
          />
        </Box>
      </Box>

      <Divider />
      <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <Box sx={{ flex: 1 }} />
        <FancyButton testId="adv_cancel_btn" onClick={() => setActiveTab('response')}>Cancel</FancyButton>
        <FancyButton testId="adv_apply_btn" solid onClick={handleApply} disabled={!modifiedSha}>
          Apply
        </FancyButton>
      </Box>

      <ConfirmModal
        open={clearVersionConfirmOpen}
        title="Clear this version?"
        body={`This removes v${currentIter + 1} and reverts to the previous version. The chat history is preserved.`}
        okLabel="Clear"
        onConfirm={clearCurrentVersion}
        onClose={() => setClearVersionConfirmOpen(false)}
      />

    </Box>
  )
}
